import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import { logout } from '../store/authSlice';
import { addBreadcrumb, captureException } from '../utils/errorReporting';
import { getGlobalApiUrl } from '../utils/globalConfig';

import type {
	BaseQueryFn,
	FetchArgs,
	FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';

// Define RootState type locally to avoid circular dependency
interface RootState {
	mfeAuth?: {
		accessToken?: string;
	};
	tenant?: {
		tenant: {
			id: string;
		} | null;
	};
}

const errorMiddleware = (result: any, api: any) => {
	if ('error' in result) {
		const error = result.error as FetchBaseQueryError;
		let errorMessage = 'An error occurred';
		let shouldShowToast = true;
		if ('status' in error) {
			switch (error.status) {
				case 401:
					errorMessage = 'Unauthorized access';
					localStorage.removeItem('auth_token');
					api.dispatch(logout());
					window.location.href = '/auth?type=login';
					break;
				case 403:
					errorMessage = 'Access forbidden';
					break;
				case 409:
				case 400:
				case 404:
					shouldShowToast = false;
					break;
				default:
					errorMessage =
						typeof error.data === 'object' && error.data !== null
							? (error.data as { error?: string }).error ||
								'An unexpected error occurred'
							: 'An unexpected error occurred';
			}
		}

		// Record API errors, but don't spam for expected errors like 401, 404.
		if ('status' in error && ![401, 404].includes(error.status as number)) {
			addBreadcrumb(
				`API Error: ${error.status} - ${errorMessage}`,
				'http',
				'error',
			);

			// For 500+ errors, capture as exceptions.
			if (error.status && Number(error.status) >= 500) {
				captureException(new Error(`API Error: ${errorMessage}`), {
					apiError: true,
					status: error.status,
					data: error.data,
					endpoint: 'unknown', // Endpoint information not available in this context
				});
			}
		}

		// Dispatch toast action for error handling
		if (shouldShowToast) {
			api.dispatch({
				type: 'toast/showToast',
				payload: {
					title: 'Error',
					description: errorMessage,
					type: 'error',
					options: { duration: 5000 },
				},
			});
		}
	}
	return result;
};

// Timeout after 20 seconds
const TIMEOUT = 60000;

// Retry configuration
const RETRY_COUNT = 3;
const RETRY_DELAY = 1000; // 1 second

export const paymentStatementBaseQuery: BaseQueryFn<
	string | FetchArgs,
	unknown,
	FetchBaseQueryError
> = async (
	args,
	api,
	extraOptions: { accessToken?: string; baseUrl?: string } & Record<
		string,
		any
	>,
) => {
	// Use global API URL if available, otherwise use extraOptions.baseUrl, then fallback to environment variable
	const baseUrl =
		getGlobalApiUrl() ||
		extraOptions?.baseUrl ||
		process.env.REACT_APP_STATEMENT_API;

	const state = api.getState() as RootState;

	if (!baseUrl) {
		return {
			error: {
				status: 'CUSTOM_ERROR',
				error: 'Payment statement API base URL not configured',
			} as FetchBaseQueryError,
		};
	}

	let retryCount = 0;
	let lastError: FetchBaseQueryError | undefined;

	while (retryCount < RETRY_COUNT) {
		try {
			const controller = new AbortController();
			const timeoutId = setTimeout(() => controller.abort(), TIMEOUT);

			const result = await fetchBaseQuery({
				baseUrl: baseUrl.endsWith('/api') ? baseUrl : `${baseUrl}/api`,
				credentials: 'include', // Required for CORS requests with credentials
				prepareHeaders: (headers, { getState }) => {
					const currentState = getState() as RootState;
					// Check for token in multiple sources: extraOptions, Redux state, and localStorage
					const currentAccessToken =
						extraOptions?.accessToken ||
						currentState.mfeAuth?.accessToken ||
						localStorage.getItem('auth_token');
					if (currentAccessToken) {
						headers.set('authorization', `Bearer ${currentAccessToken}`);
					}

					// Get tenant_id from localStorage or Redux state
					const tenantId =
						currentState.tenant?.id || localStorage.getItem('tenant_id');

					// Only set header if tenantId is a valid non-null value
					if (
						tenantId &&
						tenantId !== 'null' &&
						tenantId !== 'none' &&
						tenantId.trim() !== ''
					) {
						headers.set('x-tenant-id', tenantId);
					}

					return headers;
				},
				fetchFn: (input, init) => {
					return fetch(input, {
						...init,
						signal: controller.signal,
					});
				},
			})(args, api, extraOptions);

			clearTimeout(timeoutId);
			return errorMiddleware(result, api);
		} catch (error) {
			lastError = error as FetchBaseQueryError;
			retryCount++;

			// Add breadcrumb for retry attempts
			addBreadcrumb(
				`API request retry ${retryCount}/${RETRY_COUNT}`,
				'http',
				'warning',
			);

			if (retryCount < RETRY_COUNT) {
				await new Promise((resolve) =>
					setTimeout(resolve, RETRY_DELAY * retryCount),
				);
				continue;
			}
		}
	}

	// Capture retry failures as exceptions.
	captureException(new Error('API request failed after multiple retries'), {
		retryFailure: true,
		retryCount: RETRY_COUNT,
		lastError: lastError,
		baseUrl: getGlobalApiUrl(),
	});

	// Show error toast for retry failures
	api.dispatch({
		type: 'toast/showToast',
		payload: {
			title: 'Error',
			description: 'Request failed after multiple retries',
			type: 'error',
			options: { duration: 5000 },
		},
	});

	return {
		error:
			lastError ||
			({
				status: 'CUSTOM_ERROR',
				error: 'Request failed after retries',
			} as FetchBaseQueryError),
	};
};

// Create the main API instance for payment statement
export const paymentStatementApi = createApi({
	reducerPath: 'paymentStatementApi',
	baseQuery: paymentStatementBaseQuery,
	endpoints: () => ({}),
	tagTypes: [
		'StatementList',
		'Statement',
		'ProcessingStatus',
		'CustomRates',
		'TeamMembers',
		'UserInfo',
		'SubscriptionStatus',
		'DefaultRates',
		'OnboardingStatus',
		'EnterpriseUsers',
		'Invoices',
		'Invoice',
		'BillingConfig',
		'PaymentStatus',
		'EnterpriseSettings',
		'RevenueConfigs',
		'Tenants',
		'TenantConfig',
		'Domains',
		'Enterprises',
		'APIKeys',
		// Line Item Tags
		'LineItems',
		'LineItem',
		'LineItemAuditLogs',
		// Dimension Tags
		'FeeTypes',
		'Processors',
		'Networks',
	],
});
